import { observer } from 'mobx-react-lite'
import React, { useEffect, useMemo, useState } from 'react'
import Radom from '../../state/Radom'
import { Container } from '../../components/Animations'
import SearchMultiselect from '../../components/SearchMultiselect'
import Switch from '../../components/Switch'
import User from '../../state/User'
import { errorToast, successToast } from '../../util/Util'
import {
  ManagedPaymentMethod,
  getPaymentMethods,
} from '../../util/Managed'
import {
  ISwapSupportedAsset,
  isSwapSupportedAsset,
} from '../../util/OkxSwap'
import {
  SettingsCard,
  SettingsCardBody,
  SettingsCardDescription,
  SettingsCardHeader,
  SettingsCardTitle,
  SettingsEyebrow,
  SettingsHero,
  SettingsPage,
  SettingsRow,
  SettingsRowDescription,
  SettingsRows,
  SettingsRowTitle,
  SettingsSubtext,
  SettingsTitle,
  StatusPill,
} from './SettingsLayout'

/* eslint-disable @typescript-eslint/no-explicit-any */

interface TargetCandidate {
  key: string
  label: string
  network: string
  token: string | null
  method: ManagedPaymentMethod
}

const buildTargetCandidates = (okxSwapAssets: ISwapSupportedAsset[]): TargetCandidate[] => {
  const methods = getPaymentMethods()
  return methods
    .filter((m) => m.isSwapEnabled && !m.isTestnet && !m.isFiat && !m.isHidden && !m.isDisabled)
    .filter((m) => {
      if (okxSwapAssets.length === 0) {
        return true
      }
      const network = m.hostChain?.name ?? m.name
      const token = m.hostChain?.tokenAddress ?? null
      return isSwapSupportedAsset(network, token, okxSwapAssets)
    })
    .map((m) => {
      const network = m.hostChain?.name ?? m.name
      const token = m.hostChain?.tokenAddress ?? null
      return {
        key: `${network}:${token ?? ''}`,
        label: `${m.ticker}${m.hostChain ? ` on ${m.hostChain.name}` : ''}`,
        network,
        token,
        method: m,
      }
    })
}

const AutoSwapSettings = observer(() => {
  const [isLoading, setIsLoading] = useState(true)
  const [enabled, setEnabled] = useState(false)
  const [target, setTarget] = useState<TargetCandidate | undefined>()
  const [saving, setSaving] = useState(false)
  const [okxSwapAssets, setOkxSwapAssets] = useState<ISwapSupportedAsset[]>([])

  const candidates = useMemo(
    () => buildTargetCandidates(okxSwapAssets),
    [okxSwapAssets]
  )

  useEffect(() => {
    if (!User.organization?.features?.isSwapEnabled) {
      return
    }

    Radom.getSwapSupportedAssets()
      .then((response) => setOkxSwapAssets(response.assets))
      .catch((err: any) => {
        console.error('Failed to load OKX swap assets', err.response?.data?.error || err.message)
      })
  }, [User.organization?.features?.isSwapEnabled])

  const load = async (): Promise<void> => {
    setIsLoading(true)
    try {
      const s = await Radom.getAutoSwapOnPayment()
      setEnabled(s.enabled)
      if (s.targetNetwork) {
        const match = candidates.find(
          (c) => c.network === s.targetNetwork && (c.token ?? null) === (s.targetToken ?? null)
        )
        setTarget(match)
      } else {
        setTarget(undefined)
      }
    } catch (err: any) {
      errorToast(err.response?.data?.error || err.message || 'Failed to load settings')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  useEffect(() => {
    if (!target) {
      return
    }
    const stillValid = candidates.some((candidate) => candidate.key === target.key)
    if (!stillValid) {
      setTarget(undefined)
    }
  }, [candidates, target])

  const persist = async (next: { enabled: boolean; target?: TargetCandidate }): Promise<void> => {
    setSaving(true)
    try {
      const updated = await Radom.updateAutoSwapOnPayment({
        enabled: next.enabled,
        targetNetwork: next.target?.network ?? null,
        targetToken: next.target?.token ?? null,
      })
      setEnabled(updated.enabled)
      successToast('Auto-swap settings updated')
    } catch (err: any) {
      errorToast(err.response?.data?.error || err.message || 'Failed to update settings')
      throw err
    } finally {
      setSaving(false)
    }
  }

  const isSwapFeatureEnabled = User.organization?.features?.isSwapEnabled === true
  const disabledForOrg = !isSwapFeatureEnabled

  const onToggle = async (): Promise<void> => {
    if (disabledForOrg || saving) return
    const next = !enabled
    if (next && !target) {
      errorToast('Please pick a target currency first')
      return
    }
    setEnabled(next)
    try {
      await persist({ enabled: next, target })
    } catch {
      setEnabled(!next)
    }
  }

  const onTargetChanged = async (items: TargetCandidate[]): Promise<void> => {
    const chosen = items[0]
    setTarget(chosen)
    if (enabled) {
      try {
        await persist({ enabled, target: chosen })
      } catch {
        load()
      }
    }
  }

  const pillTone: 'green' | 'amber' | 'neutral' = enabled ? 'green' : disabledForOrg ? 'amber' : 'neutral'
  const pillLabel = disabledForOrg ? 'Swap not enabled for organization' : enabled ? 'On' : 'Off'

  return (
    <Container>
      <SettingsPage>
        <SettingsHero>
          <div>
            <SettingsEyebrow>Organization settings</SettingsEyebrow>
            <SettingsTitle>Auto-swap on payment</SettingsTitle>
            <SettingsSubtext>
              When this is on, every incoming payment or deposit is automatically converted into the
              currency you choose below. The swap runs in the background and your existing balances
              are credited normally if anything goes wrong.
            </SettingsSubtext>
          </div>
          <StatusPill $tone={pillTone}>{pillLabel}</StatusPill>
        </SettingsHero>

        <SettingsCard>
          <SettingsCardHeader>
            <div>
              <SettingsCardTitle>Auto-swap toggle</SettingsCardTitle>
              <SettingsCardDescription>
                {disabledForOrg
                  ? 'Swap is not enabled for your organization. Contact support to enable.'
                  : 'Turn this on once a target currency is selected. We never block your payment receipt on the swap completing.'}
              </SettingsCardDescription>
            </div>
          </SettingsCardHeader>
          <SettingsCardBody>
            <SettingsRows>
              <SettingsRow>
                <div>
                  <SettingsRowTitle>Convert incoming payments</SettingsRowTitle>
                  <SettingsRowDescription>
                    Each credited balance is queued for an auto-swap. Payments already in the target
                    currency are skipped.
                  </SettingsRowDescription>
                </div>
                <Switch
                  checked={enabled}
                  disabled={disabledForOrg || saving || isLoading}
                  onClick={onToggle}
                />
              </SettingsRow>
              <SettingsRow>
                <div>
                  <SettingsRowTitle>Target currency</SettingsRowTitle>
                  <SettingsRowDescription>
                    The currency every supported incoming payment will be auto-swapped into.
                  </SettingsRowDescription>
                </div>
                <div style={{ minWidth: 280 }}>
                  <SearchMultiselect
                    isLoading={isLoading}
                    disabled={disabledForOrg || saving}
                    placeholder="Select currency"
                    itemArray={candidates}
                    selectedItems={target ? [target] : []}
                    setSelectedItems={onTargetChanged}
                    keyExtractor={(c: TargetCandidate) => c.key}
                    labelExtractor={(c: TargetCandidate) => (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        {c.method.logo && (
                          <img
                            src={c.method.logo}
                            alt={c.label}
                            style={{ width: 16, height: 16, borderRadius: 4 }}
                          />
                        )}
                        {c.label}
                      </div>
                    )}
                    isSingleSelect
                  />
                </div>
              </SettingsRow>
            </SettingsRows>
          </SettingsCardBody>
        </SettingsCard>
      </SettingsPage>
    </Container>
  )
})

export default AutoSwapSettings
